import type { PluginListenerHandle } from "@capacitor/core"
import { Filesystem } from "@capacitor/filesystem"
import {
	RecorderError,
	toPermission,
	type VoiceRecorder,
} from "./voice-recorder.base"

const NATIVE_BIT_RATE = 32_000
const NATIVE_MIME_TYPE = "audio/mp4"
const NATIVE_FILE_NAME = "voice.m4a"

function loadNativeModule() {
	return import("@capgo/capacitor-audio-recorder")
}

function base64ToBlob(base64: string, mimeType: string): Blob {
	const binary = atob(base64)
	const bytes = new Uint8Array(binary.length)
	for (let index = 0; index < binary.length; index += 1) {
		bytes[index] = binary.charCodeAt(index)
	}
	return new Blob([bytes], { type: mimeType })
}

async function readRecordedFile(uri: string): Promise<Blob> {
	try {
		const { data } = await Filesystem.readFile({ path: uri })
		if (typeof data !== "string") {
			return new Blob([data], { type: NATIVE_MIME_TYPE })
		}
		return base64ToBlob(data, NATIVE_MIME_TYPE)
	} finally {
		await Filesystem.deleteFile({ path: uri }).catch(() => {})
	}
}

export function createNativeRecorder(): VoiceRecorder {
	return {
		async checkPermission() {
			const { CapacitorAudioRecorder } = await loadNativeModule()
			const { recordAudio } = await CapacitorAudioRecorder.checkPermissions()
			return toPermission(recordAudio)
		},

		async requestPermission() {
			const { CapacitorAudioRecorder } = await loadNativeModule()
			const { recordAudio } = await CapacitorAudioRecorder.requestPermissions()
			return toPermission(recordAudio)
		},

		async start() {
			const { CapacitorAudioRecorder, AudioSessionMode } =
				await loadNativeModule()
			await CapacitorAudioRecorder.startRecording({
				bitRate: NATIVE_BIT_RATE,
				audioSessionMode: AudioSessionMode.Default,
			})
		},

		async stop() {
			const { CapacitorAudioRecorder } = await loadNativeModule()
			const result = await CapacitorAudioRecorder.stopRecording()
			if (!result.uri) {
				throw new RecorderError("noData")
			}

			const blob = await readRecordedFile(result.uri)
			if (blob.size === 0) {
				throw new RecorderError("empty")
			}

			return {
				blob,
				durationMs: result.duration ?? 0,
				fileName: NATIVE_FILE_NAME,
			}
		},

		async cancel() {
			const { CapacitorAudioRecorder } = await loadNativeModule()
			await CapacitorAudioRecorder.cancelRecording()
		},

		onInterruption(listener) {
			const handles = loadNativeModule()
				.then(({ CapacitorAudioRecorder: plugin }) =>
					Promise.all([
						plugin.addListener("recordingError", () => listener()),
						plugin.addListener("recordingInterruptionBegan", () => listener()),
					]),
				)
				.catch((): PluginListenerHandle[] => [])

			return () => {
				void handles.then((added) => {
					for (const handle of added) {
						void handle.remove().catch(() => {})
					}
				})
			}
		},
	}
}
